"use client";

import React, { useEffect, useRef, useState } from "react";
import { Plus } from "lucide-react";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.15,
  rootMargin = "50px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const galleryItems = [{
  id: 1,
  src: "https://optimum-pug.10web.cloud/wp-content/uploads/2026/05/Newsom-after-2.jpg?v=b436ehmq1z4",
  alt: "Forestry mulching transformation in Burlington",
  year: "2024",
  spanClass: "md:col-span-12 aspect-video"
}, {
  id: 2,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678813/img/newsombefore-after-quadrant.png",
  alt: "Professional land clearing results",
  year: "2024",
  spanClass: "md:col-span-7 aspect-square"
}];
function GalleryItem({
  item,
  index
}: {
  item: (typeof galleryItems)[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <div ref={ref} data-index={index} className={`relative group overflow-hidden bg-muted rounded-2xl shadow-md ${item.spanClass} transition-all duration-1000 ease-out will-change-transform ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
      <img src={item.src} alt={item.alt} className="object-cover transition-transform duration-[1.5s] ease-in-out group-hover:scale-105 ![padding:0px_0px_86px_0px] !w-[1024px] !h-[1024px]" loading={index < 2 ? "eager" : "lazy"} />
      
      {}
      <div className="absolute inset-0 bg-secondary/60 opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4 transform translate-y-4 group-hover:translate-y-0 transition-transform duration-700 ease-out">
          <div className="w-12 h-12 rounded-lg border border-primary/30 flex items-center justify-center bg-background/10 backdrop-blur-md">
            <Plus className="w-6 h-6 text-primary" strokeWidth={2.5} />
          </div>
          <span className="font-default text-sm font-bold tracking-widest text-secondary-foreground uppercase">
            Project
          </span>
        </div>
      </div>
    </div>;
}
export default function VisualNarrative() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll();
  return;
}